import {
    PulseStorage,
    StorageChangeKeyMatch,
    StorageChangeOrigin,
    deepObjectEqual,
} from '@pulse/common';
import { Observable, ReplaySubject } from 'rxjs';
import { TabManagerService } from './tab/tab-manager';
import { TrafficSourceService } from './traffic.source/traffic-source.service';
import { WebToMobileService } from './w2m.service';

export class StateService {
    private state?: Record<string, unknown>;
    private stateSubject$ = new ReplaySubject<Record<string, unknown>>(1);

    constructor(
        private storage: PulseStorage,
        private storageKey: string,
        private webToMobileService: WebToMobileService,
        private trafficSourceService: TrafficSourceService,
        private tabManagerService: TabManagerService,
        public storageChangeOrigin: StorageChangeOrigin,
    ) {
        this.registerStorageChangeListeners();

        const initialState = this.getStateInternal();
        this.stateSubject$.next(initialState);
    }

    setState(state: Record<string, unknown>): void {
        const currentState = this.getStateInternal();
        const mergedState: Record<string, unknown> = {
            ...currentState,
            ...state,
        };

        if (deepObjectEqual(currentState, mergedState)) {
            return;
        }

        this.state = mergedState;
        this.storage.setJSON(this.storageKey, mergedState);
        this.stateSubject$.next(mergedState);
    }

    getState(): Record<string, unknown> {
        const isW2M = Boolean(this.webToMobileService.getWebToMobileMetaInfo());
        const referrer = this.trafficSourceService.getReferrer()
            ? this.trafficSourceService.getReferrer()?.url
            : undefined;
        const internalState = { ...this.getStateInternal() };
        const data = internalState['data']
            ? { ...internalState['data'] }
            : ({} as Record<string, unknown>);
        data['is_w2m'] = isW2M;
        data['referrer'] = referrer;
        internalState['data'] = data;
        internalState['tab_id'] = this.tabManagerService.getTabId();

        return internalState;
    }

    getStateInternal(): Record<string, unknown> {
        if (!this.state) {
            return this.refreshState();
        }

        return this.state;
    }

    getState$(): Observable<Record<string, unknown>> {
        return this.stateSubject$.asObservable();
    }

    removePropertyFromState(property: string): void {
        const currentState = this.getStateInternal();

        if (property in currentState) {
            const newState = { ...currentState };
            delete newState[property];

            if (!deepObjectEqual(currentState, newState)) {
                this.state = newState;
                this.storage.setJSON(this.storageKey, newState);
                this.stateSubject$.next(newState);
            }
        }
    }

    private refreshState(providedState?: Record<string, unknown>): Record<string, unknown> {
        const state = providedState || this.storage.getJSON(this.storageKey) || this.state || {};

        const isW2M = Boolean(this.webToMobileService.getWebToMobileMetaInfo());
        const data = (state['data'] || {}) as Record<string, unknown>;
        data['is_w2m'] = isW2M;
        state['data'] = data;
        this.state = state;

        return state;
    }

    private registerStorageChangeListeners(): void {
        this.storage
            .change(this.storageKey, StorageChangeKeyMatch.Full, this.storageChangeOrigin)
            .subscribe(({ value }) => {
                const parsedState = JSON.parse(value || '{}');
                const previousState = this.getStateInternal();
                const updatedState = this.refreshState(parsedState);

                if (!deepObjectEqual(previousState, updatedState)) {
                    this.stateSubject$.next(updatedState);
                }
            });
    }
}
